import React, { useState, useEffect, useRef } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Settings,
  X,
  MoreVertical,
  Download,
  FileText,
  RefreshCw,
  Database,
  ChevronRightIcon,
  ExternalLink,
} from 'lucide-react'
import styles from './DataTable.module.scss'
import { ALL_FIELDS } from './AdvancedSearch'
import NotificationModal, { NotificationType } from './NotificationModal'
import { config } from '../../config'

interface Column {
  field: string
  label: string
  display: boolean
}

interface DataTableProps {
  data: any[]
  total: number
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  loading?: boolean
  isToday?: boolean
  datePattern?: string
}

const DEFAULT_COLUMNS: Column[] = [
  { field: 'correlation_id', label: 'Correlation ID', display: true },
  { field: 'vin', label: 'VIN', display: true },
  { field: 'relations.generation.title', label: 'Generation', display: true },
  { field: 'relations.model.make', label: 'Make', display: true },
  { field: 'http_legacy_status', label: 'Status', display: true },
]

const STORAGE_KEY = 'insights_table_columns'

// Transform ES file paths to served URLs
const transformFilePath = (path: string | null): string | null => {
  if (!path) return null
  // /data/processed/... -> {ARTIFACTS_URL}/processed/...
  return path.replace('/data/', `${config.ARTIFACTS_URL}/`)
}

const transformBatchFilePath = (path: string | null): string | null => {
  if (!path) return null
  // /data/res/batches/... -> {ARTIFACTS_URL}/results/batches/...
  return path.replace('/data/res/', `${config.ARTIFACTS_URL}/results/`)
}

// Generate alias for URL (feed_import source uses double dash)
const generateAlias = (name: string): string => {
  if (!name) return ''
  // For feed_import source, useDoubleDash = true
  const replacementChar = '--'
  // Replace all non-alphanumeric characters (except hyphen) with replacement char
  const result = name.replace(/[^a-zA-Z0-9-]/g, replacementChar)
  return result.toLowerCase()
}

// Build car listing URL for dupontregistry.com
const buildCarListingUrl = (row: any): string | null => {
  const dbRecordId = row.db_record_id
  const year = row.year
  const make = row.relations?.model?.make
  const model = row.relations?.generation?.title

  // All fields are required
  if (!dbRecordId || !year || !make || !model) return null

  const makeAlias = generateAlias(make)
  const modelAlias = generateAlias(model)

  return `https://dupontregistry.com/autos/listing/${year}/${makeAlias}/${modelAlias}/${dbRecordId}`
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  total,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading = false,
  isToday = false,
  datePattern,
}) => {
  const [columns, setColumns] = useState<Column[]>(DEFAULT_COLUMNS)
  const [showConfig, setShowConfig] = useState(false)
  const [addFieldQuery, setAddFieldQuery] = useState('')
  const [showAddDropdown, setShowAddDropdown] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const [openActionMenu, setOpenActionMenu] = useState<number | null>(null)
  const [openSubmenu, setOpenSubmenu] = useState<'retry' | 'dbcheck' | null>(null)
  const [retryingRows, setRetryingRows] = useState<Set<string>>(new Set())
  const [notification, setNotification] = useState<{
    isOpen: boolean
    type: NotificationType
    title: string
    message: string
  }>({ isOpen: false, type: 'info', title: '', message: '' })
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null)

  const addInputRef = useRef<HTMLInputElement>(null)
  const addDropdownRef = useRef<HTMLDivElement>(null)
  const actionMenuRef = useRef<HTMLDivElement>(null)
  const actionButtonRefs = useRef<Map<number, HTMLButtonElement>>(new Map())

  const showNotification = (type: NotificationType, title: string, message: string) => {
    setNotification({ isOpen: true, type, title, message })
  }

  const closeNotification = () => {
    setNotification((prev) => ({ ...prev, isOpen: false }))
  }

  const toggleActionMenu = (rowIdx: number) => {
    if (openActionMenu === rowIdx) {
      setOpenActionMenu(null)
      setMenuPosition(null)
      setOpenSubmenu(null)
      return
    }

    const button = actionButtonRefs.current.get(rowIdx)
    if (button) {
      const rect = button.getBoundingClientRect()
      const menuHeight = 200 // reduced since we're using submenus
      const spaceBelow = window.innerHeight - rect.bottom
      const spaceAbove = rect.top

      let top: number
      if (spaceBelow >= menuHeight || spaceBelow > spaceAbove) {
        // Open below
        top = rect.bottom + 4
      } else {
        // Open above
        top = rect.top - menuHeight - 4
      }

      setMenuPosition({
        top: Math.max(8, Math.min(top, window.innerHeight - menuHeight - 8)),
        left: Math.min(rect.right - 200, window.innerWidth - 210),
      })
    }
    setOpenActionMenu(rowIdx)
    setOpenSubmenu(null)
  }

  // Retry entity push
  const handleRetryPush = async (correlationId: string, entityType: string) => {
    if (!datePattern) return

    setRetryingRows((prev) => new Set(prev).add(`${correlationId}-retry`))
    setOpenActionMenu(null)
    setOpenSubmenu(null)
    try {
      const res = await fetch(`${config.MONITOR_API_URL}/insights/retry-entity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          correlation_id: correlationId,
          entity_type: entityType,
          date: datePattern,
        }),
      })
      const data = await res.json()
      if (data.success) {
        showNotification('success', 'Retry Queued', `Retry has been queued for ${entityType}.`)
      } else {
        showNotification('error', 'Retry Failed', data.error || 'An error occurred.')
      }
    } catch (e) {
      console.error('Retry failed', e)
      showNotification('error', 'Retry Failed', 'Failed to queue retry. Please try again.')
    } finally {
      setRetryingRows((prev) => {
        const next = new Set(prev)
        next.delete(`${correlationId}-retry`)
        return next
      })
    }
  }

  // DB Check entity
  const handleDbCheck = async (correlationId: string, entityType: string) => {
    if (!datePattern) return

    setRetryingRows((prev) => new Set(prev).add(`${correlationId}-dbcheck`))
    setOpenActionMenu(null)
    setOpenSubmenu(null)
    try {
      const res = await fetch(`${config.MONITOR_API_URL}/insights/db-check-entity`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          correlation_id: correlationId,
          entity_type: entityType,
          date: datePattern,
        }),
      })
      const data = await res.json()
      if (data.success) {
        showNotification('success', 'DB Check Queued', `DB check has been queued for ${entityType}.`)
      } else {
        showNotification('error', 'DB Check Failed', data.error || 'An error occurred.')
      }
    } catch (e) {
      console.error('DB check failed', e)
      showNotification('error', 'DB Check Failed', 'Failed to queue DB check. Please try again.')
    } finally {
      setRetryingRows((prev) => {
        const next = new Set(prev)
        next.delete(`${correlationId}-dbcheck`)
        return next
      })
    }
  }

  // Get available entities for a row
  const getAvailableEntities = (row: any) => {
    const entities: { type: string; label: string }[] = [{ type: 'car', label: 'Car' }]
    if (row.relations?.dealer?.id) entities.push({ type: 'dealer', label: 'Dealer' })
    if (row.relations?.model?.id) entities.push({ type: 'model', label: 'Model' })
    if (row.relations?.modification?.id) entities.push({ type: 'modification', label: 'Modification' })
    if (row.relations?.generation?.id) entities.push({ type: 'generation', label: 'Generation' })
    return entities
  }

  // Get pushable entities (excludes dealer which doesn't have retry)
  const getPushableEntities = (row: any) => {
    const entities: { type: string; label: string }[] = [{ type: 'car', label: 'Car' }]
    if (row.relations?.model?.id) entities.push({ type: 'model', label: 'Model' })
    if (row.relations?.modification?.id) entities.push({ type: 'modification', label: 'Modification' })
    if (row.relations?.generation?.id) entities.push({ type: 'generation', label: 'Generation' })
    return entities
  }

  // Retry all pushable entities
  const handleRetryAllPush = async (correlationId: string, row: any) => {
    if (!datePattern) return

    const entities = getPushableEntities(row)
    setRetryingRows((prev) => new Set(prev).add(`${correlationId}-retry`))
    setOpenActionMenu(null)
    setOpenSubmenu(null)

    try {
      const results = await Promise.all(
        entities.map((entity) =>
          fetch(`${config.MONITOR_API_URL}/insights/retry-entity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              correlation_id: correlationId,
              entity_type: entity.type,
              date: datePattern,
            }),
          }).then((res) => res.json()),
        ),
      )

      const successCount = results.filter((r) => r.success).length
      if (successCount === entities.length) {
        showNotification('success', 'Retry Queued', `Retry has been queued for all ${entities.length} entities.`)
      } else if (successCount > 0) {
        showNotification('warning', 'Partial Success', `Retry queued for ${successCount}/${entities.length} entities.`)
      } else {
        showNotification('error', 'Retry Failed', 'Failed to queue retry for any entity.')
      }
    } catch (e) {
      console.error('Retry all failed', e)
      showNotification('error', 'Retry Failed', 'Failed to queue retry. Please try again.')
    } finally {
      setRetryingRows((prev) => {
        const next = new Set(prev)
        next.delete(`${correlationId}-retry`)
        return next
      })
    }
  }

  // DB Check all entities
  const handleDbCheckAll = async (correlationId: string, row: any) => {
    if (!datePattern) return

    const entities = getAvailableEntities(row)
    setRetryingRows((prev) => new Set(prev).add(`${correlationId}-dbcheck`))
    setOpenActionMenu(null)
    setOpenSubmenu(null)

    try {
      const results = await Promise.all(
        entities.map((entity) =>
          fetch(`${config.MONITOR_API_URL}/insights/db-check-entity`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              correlation_id: correlationId,
              entity_type: entity.type,
              date: datePattern,
            }),
          }).then((res) => res.json()),
        ),
      )

      const successCount = results.filter((r) => r.success).length
      if (successCount === entities.length) {
        showNotification('success', 'DB Check Queued', `DB check has been queued for all ${entities.length} entities.`)
      } else if (successCount > 0) {
        showNotification(
          'warning',
          'Partial Success',
          `DB check queued for ${successCount}/${entities.length} entities.`,
        )
      } else {
        showNotification('error', 'DB Check Failed', 'Failed to queue DB check for any entity.')
      }
    } catch (e) {
      console.error('DB check all failed', e)
      showNotification('error', 'DB Check Failed', 'Failed to queue DB check. Please try again.')
    } finally {
      setRetryingRows((prev) => {
        const next = new Set(prev)
        next.delete(`${correlationId}-dbcheck`)
        return next
      })
    }
  }

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved) {
      try {
        setColumns(JSON.parse(saved))
      } catch (e) {
        console.error('Failed to load column config', e)
      }
    }
  }, [])

  useEffect(() => {
    setHighlightedIndex(0)
  }, [addFieldQuery])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target as Node)) {
        setShowAddDropdown(false)
      }
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target as Node)) {
        setOpenActionMenu(null)
        setMenuPosition(null)
        setOpenSubmenu(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const saveColumns = (newColumns: Column[]) => {
    setColumns(newColumns)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newColumns))
  }

  const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc?.[part], obj)
  }

  // Fields not already in columns
  const availableFields = ALL_FIELDS.filter((f) => !columns.some((c) => c.field === f.value))

  const filteredAddFields = availableFields.filter(
    (f) =>
      f.value.toLowerCase().includes(addFieldQuery.toLowerCase()) ||
      f.label.toLowerCase().includes(addFieldQuery.toLowerCase()),
  )

  const addColumn = (fieldValue: string) => {
    const field = ALL_FIELDS.find((f) => f.value === fieldValue)
    if (!field) return

    // Check for duplicate labels and make unique
    let label = field.label
    let suffix = 1
    while (columns.some((c) => c.label === label)) {
      label = `${field.label} (${suffix})`
      suffix++
    }

    const newColumn: Column = {
      field: fieldValue,
      label: label,
      display: true,
    }
    saveColumns([...columns, newColumn])
    setAddFieldQuery('')
    setShowAddDropdown(false)
  }

  const removeColumn = (index: number) => {
    const updated = columns.filter((_, i) => i !== index)
    saveColumns(updated)
  }

  const handleAddKeyDown = (e: React.KeyboardEvent) => {
    if (!showAddDropdown) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.min(prev + 1, filteredAddFields.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filteredAddFields.length > 0) {
        addColumn(filteredAddFields[highlightedIndex].value)
      }
    } else if (e.key === 'Escape') {
      setShowAddDropdown(false)
    }
  }

  const handleDownload = (url: string | null, filename: string) => {
    if (!url) return
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.target = '_blank'
    a.click()
  }

  const visibleColumns = columns.filter((c) => c.display)
  const totalPages = Math.ceil(total / pageSize)
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1
  const endItem = Math.min(page * pageSize, total)

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.info}>
          <span className={styles.count}>{total.toLocaleString()}</span> results
          {total > 0 && (
            <span className={styles.range}>
              (showing {startItem.toLocaleString()} - {endItem.toLocaleString()})
            </span>
          )}
        </div>
        <button className={styles.configBtn} onClick={() => setShowConfig(!showConfig)}>
          <Settings size={16} />
          Columns ({visibleColumns.length})
        </button>
      </div>

      {showConfig && (
        <>
          <div className={styles.modalOverlay} onClick={() => setShowConfig(false)} />
          <div className={styles.configModal}>
            <div className={styles.modalHeader}>
              <h4>Column Configuration</h4>
              <button className={styles.modalClose} onClick={() => setShowConfig(false)}>
                <X size={18} />
              </button>
            </div>

            {/* Add new column */}
            <div className={styles.addColumnSection}>
              <div className={styles.addColumnWrapper} ref={addDropdownRef}>
                <input
                  ref={addInputRef}
                  type="text"
                  value={addFieldQuery}
                  onChange={(e) => {
                    setAddFieldQuery(e.target.value)
                    setShowAddDropdown(true)
                  }}
                  onFocus={() => setShowAddDropdown(true)}
                  onKeyDown={handleAddKeyDown}
                  placeholder="Search and add a column..."
                  className={styles.addColumnInput}
                />
                {showAddDropdown && filteredAddFields.length > 0 && (
                  <div className={styles.addDropdown}>
                    {filteredAddFields.slice(0, 10).map((field, idx) => (
                      <div
                        key={field.value}
                        className={`${styles.addDropdownItem} ${idx === highlightedIndex ? styles.highlighted : ''}`}
                        onClick={() => addColumn(field.value)}
                        onMouseEnter={() => setHighlightedIndex(idx)}>
                        <span className={styles.addFieldLabel}>{field.label}</span>
                        <span className={styles.addFieldPath}>{field.value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Column list */}
            <div className={styles.columnList}>
              {columns.map((col, idx) => (
                <div key={`${col.field}-${idx}`} className={styles.columnItem}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={col.display}
                      onChange={(e) => {
                        const updated = [...columns]
                        updated[idx].display = e.target.checked
                        saveColumns(updated)
                      }}
                    />
                    <span className={styles.checkmark} />
                  </label>
                  <input
                    type="text"
                    value={col.label}
                    onChange={(e) => {
                      const updated = [...columns]
                      updated[idx].label = e.target.value
                      saveColumns(updated)
                    }}
                    className={styles.labelInput}
                  />
                  <span className={styles.field}>{col.field}</span>
                  <button className={styles.removeColumnBtn} onClick={() => removeColumn(idx)} title="Remove column">
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>

            <div className={styles.modalFooter}>
              <button
                onClick={() => {
                  saveColumns(DEFAULT_COLUMNS)
                }}
                className={styles.resetBtn}>
                Reset to Default
              </button>
              <button onClick={() => setShowConfig(false)} className={styles.closeBtn}>
                Done
              </button>
            </div>
          </div>
        </>
      )}

      <div className={styles.tableWrapper}>
        {loading ? (
          <div className={styles.loading}>
            <div className={styles.spinner} />
            Loading data...
          </div>
        ) : data.length === 0 ? (
          <div className={styles.empty}>
            <p>No results found</p>
            <span>Try adjusting your filters or search criteria</span>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                {visibleColumns.map((col) => (
                  <th key={col.field}>{col.label}</th>
                ))}
                <th className={styles.actionsHeader}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIdx) => {
                const originFileUrl = transformFilePath(row.file_path)
                const batchFileUrl = transformBatchFilePath(row.batch_file_path)
                const correlationId = row.correlation_id
                const isRetrying =
                  retryingRows.has(`${correlationId}-retry`) || retryingRows.has(`${correlationId}-dbcheck`)
                const carListingUrl = buildCarListingUrl(row)

                return (
                  <tr key={rowIdx}>
                    {visibleColumns.map((col) => (
                      <td key={col.field}>{getNestedValue(row, col.field) ?? '-'}</td>
                    ))}
                    <td
                      className={`${styles.actionsCell} ${openActionMenu === rowIdx ? styles.actionsCellActive : ''}`}>
                      <div className={styles.actionWrapper}>
                        {carListingUrl && (
                          <a
                            href={carListingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.viewOnSiteBtn}
                            title="View on DuPont Registry">
                            <ExternalLink size={16} />
                          </a>
                        )}
                        <button
                          ref={(el) => {
                            if (el) actionButtonRefs.current.set(rowIdx, el)
                          }}
                          className={styles.actionBtn}
                          onClick={() => toggleActionMenu(rowIdx)}
                          disabled={isRetrying}>
                          {isRetrying ? (
                            <RefreshCw size={16} className={styles.spinning} />
                          ) : (
                            <MoreVertical size={16} />
                          )}
                        </button>
                        {openActionMenu === rowIdx && menuPosition && (
                          <div
                            ref={actionMenuRef}
                            className={styles.actionMenu}
                            style={{ top: menuPosition.top, left: menuPosition.left }}>
                            {/* Downloads - only for today */}
                            {isToday && originFileUrl && (
                              <button
                                className={styles.actionItem}
                                onClick={() => {
                                  handleDownload(originFileUrl, `origin_${row.vin || 'file'}.csv`)
                                  setOpenActionMenu(null)
                                }}>
                                <FileText size={14} />
                                Download Origin File
                              </button>
                            )}
                            {isToday && batchFileUrl && (
                              <button
                                className={styles.actionItem}
                                onClick={() => {
                                  handleDownload(batchFileUrl, `batch_${row.vin || 'file'}.json`)
                                  setOpenActionMenu(null)
                                }}>
                                <Download size={14} />
                                Download Batch File
                              </button>
                            )}

                            {/* Retry Push with submenu */}
                            <div className={styles.submenuWrapper}>
                              <button
                                className={`${styles.actionItem} ${styles.hasSubmenu} ${
                                  openSubmenu === 'retry' ? styles.active : ''
                                }`}
                                onClick={() => setOpenSubmenu(openSubmenu === 'retry' ? null : 'retry')}>
                                <RefreshCw size={14} />
                                Retry Push
                                <ChevronRightIcon size={14} className={styles.submenuArrow} />
                              </button>
                              {openSubmenu === 'retry' && (
                                <div className={styles.submenu}>
                                  <button
                                    className={styles.actionItem}
                                    onClick={() => handleRetryAllPush(correlationId, row)}>
                                    <RefreshCw size={14} />
                                    Retry All
                                  </button>
                                  <div className={styles.submenuDivider} />
                                  {getPushableEntities(row).map((entity) => (
                                    <button
                                      key={entity.type}
                                      className={styles.actionItem}
                                      onClick={() => handleRetryPush(correlationId, entity.type)}>
                                      {entity.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* DB Check with submenu */}
                            <div className={styles.submenuWrapper}>
                              <button
                                className={`${styles.actionItem} ${styles.hasSubmenu} ${
                                  openSubmenu === 'dbcheck' ? styles.active : ''
                                }`}
                                onClick={() => setOpenSubmenu(openSubmenu === 'dbcheck' ? null : 'dbcheck')}>
                                <Database size={14} />
                                DB Check
                                <ChevronRightIcon size={14} className={styles.submenuArrow} />
                              </button>
                              {openSubmenu === 'dbcheck' && (
                                <div className={styles.submenu}>
                                  <button
                                    className={styles.actionItem}
                                    onClick={() => handleDbCheckAll(correlationId, row)}>
                                    <Database size={14} />
                                    Check All
                                  </button>
                                  <div className={styles.submenuDivider} />
                                  {getAvailableEntities(row).map((entity) => (
                                    <button
                                      key={entity.type}
                                      className={styles.actionItem}
                                      onClick={() => handleDbCheck(correlationId, entity.type)}>
                                      {entity.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <div className={styles.pagination}>
          <div className={styles.pageSize}>
            <span>Rows per page:</span>
            <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>

          <div className={styles.pageControls}>
            <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
              <ChevronLeft size={18} />
            </button>
            <span className={styles.pageInfo}>
              Page <strong>{page}</strong> of <strong>{totalPages || 1}</strong>
            </span>
            <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      )}

      <NotificationModal
        isOpen={notification.isOpen}
        type={notification.type}
        title={notification.title}
        message={notification.message}
        onClose={closeNotification}
      />
    </div>
  )
}

export default DataTable
